import { cn } from '@atlas/ui-primitives'

/**
 * Marks a region of a page whose content is sample data because the concept has no
 * backing model yet.
 *
 * Deliberately unmissable rather than tasteful: a surface carrying one may be shown to
 * a client, and a demo that reads as truth is worse than a visible gap. The cost of
 * retiring it is one deleted prop on the card that renders it.
 */
export interface SampleDataNoticeProps {
	/** What would replace the sample data, e.g. "a brand-kit model". Shown as a hint. */
	pending?: string
	className?: string
}

/**
 * Not a `Badge` with a className: every Badge variant is a solid fill, and this
 * is deliberately an outline so it reads as an annotation on the data rather
 * than a status of it. A published component with its own dot and title, not
 * stray markup — if an outline emphasis is ever wanted twice, that is the point
 * to add the variant.
 */
export function SampleDataNotice({ pending, className }: SampleDataNoticeProps) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 rounded-ds-full border border-ds-warning px-2 py-0.5',
				'font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-warning',
				className,
			)}
			title={pending ? `Sample data — awaiting ${pending}` : 'Sample data — not backed by real records'}
		>
			<span aria-hidden className="h-1.5 w-1.5 rounded-ds-full bg-ds-warning" />
			Sample data
		</span>
	)
}
